import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";

type RefreshHandler = () => void | Promise<void>;

interface RefreshRegistration {
  id: symbol;
  handler: RefreshHandler;
  disabled: boolean;
  disabledReason: string;
}

interface ViewRefreshContextValue {
  registration: RefreshRegistration | null;
  register: (registration: RefreshRegistration) => void;
  unregister: (id: symbol) => void;
  update: (id: symbol, update: Pick<RefreshRegistration, "disabled" | "disabledReason">) => void;
}

interface ViewRefreshState {
  available: boolean;
  disabled: boolean;
  disabledReason: string;
  refreshing: boolean;
  status: string;
  statusType: "success" | "error" | null;
  refresh: () => Promise<void>;
}

const ViewRefreshContext = createContext<ViewRefreshContextValue | null>(null);

export function ViewRefreshProvider({ children }: { children: ReactNode }) {
  const [registration, setRegistration] = useState<RefreshRegistration | null>(null);

  const register = useCallback((nextRegistration: RefreshRegistration) => {
    setRegistration(nextRegistration);
  }, []);

  const unregister = useCallback((id: symbol) => {
    setRegistration((current) => (current?.id === id ? null : current));
  }, []);

  const update = useCallback(
    (id: symbol, next: Pick<RefreshRegistration, "disabled" | "disabledReason">) => {
      setRegistration((current) => (current?.id === id ? { ...current, ...next } : current));
    },
    []
  );

  const value = useMemo(
    () => ({ registration, register, unregister, update }),
    [registration, register, unregister, update]
  );

  return <ViewRefreshContext.Provider value={value}>{children}</ViewRefreshContext.Provider>;
}

export function useRegisterViewRefresh(
  handler: RefreshHandler,
  options: { disabled?: boolean; disabledReason?: string } = {}
) {
  const context = useContext(ViewRefreshContext);
  const idRef = useRef(Symbol("view-refresh"));
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!context) return;
    const id = idRef.current;
    context.register({
      id,
      handler: () => handlerRef.current(),
      disabled: Boolean(options.disabled),
      disabledReason: options.disabledReason ?? ""
    });
    return () => context.unregister(id);
    // The handler is read through a ref so screens do not re-register on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [context?.register, context?.unregister]);

  useEffect(() => {
    if (!context) return;
    context.update(idRef.current, {
      disabled: Boolean(options.disabled),
      disabledReason: options.disabledReason ?? ""
    });
  }, [context?.update, options.disabled, options.disabledReason]);
}

export function useViewRefreshState(): ViewRefreshState {
  const context = useContext(ViewRefreshContext);
  if (!context) {
    throw new Error("View refresh state must be used inside ViewRefreshProvider.");
  }
  const { registration } = context;
  const [refreshing, setRefreshing] = useState(false);
  const [status, setStatus] = useState("");
  const [statusType, setStatusType] = useState<"success" | "error" | null>(null);

  useEffect(() => {
    if (!status || refreshing) return;
    const timeoutId = window.setTimeout(() => {
      setStatus("");
      setStatusType(null);
    }, 4000);
    return () => window.clearTimeout(timeoutId);
  }, [refreshing, status]);

  const refresh = useCallback(async () => {
    if (!registration || registration.disabled || refreshing) return;

    setRefreshing(true);
    setStatus("Refreshing current view…");
    setStatusType(null);
    try {
      await registration.handler();
      setStatus("View updated just now.");
      setStatusType("success");
    } catch {
      setStatus("Couldn’t refresh this view. Try again.");
      setStatusType("error");
    } finally {
      setRefreshing(false);
    }
  }, [refreshing, registration]);

  return {
    available: Boolean(registration),
    disabled: Boolean(registration?.disabled),
    disabledReason: registration?.disabledReason ?? "",
    refreshing,
    status,
    statusType,
    refresh
  };
}

export function useUnsavedChangesWarning(enabled: boolean) {
  useEffect(() => {
    if (!enabled) return;

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", warnBeforeUnload);
    return () => window.removeEventListener("beforeunload", warnBeforeUnload);
  }, [enabled]);
}
